import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { LoginService } from '../../login/login.service';
import { VoipScheduleRulesService } from '../_services/voip-schedule-rules.service';
import { VoipHolidaysService } from '../_services/voip-holidays.service';
import { VoipHoliday } from '../_types/voip-holiday';
import { VoipScheduleRuleRow } from '../_types/voip-schedule-rule-row';
import moment from 'moment';
import { CalendarEvent } from '../../calendars/_types/calendar-event';
import { VoipScheduleRule } from '../_types/voip-schedule-rule';

@Component({
    selector: 'app-voip-schedule-config',
    templateUrl: './voip-schedule-config.component.html',
    styleUrls: ['./voip-schedule-config.component.css'],
    standalone: false
})
export class VoipScheduleConfigComponent implements OnInit {
  public allowWrite: boolean = false;
  public normalRules: VoipScheduleRuleRow[] = [];
  public guardRules: VoipScheduleRuleRow[] = [];
  public holidayEvents: CalendarEvent[] = [];
  public voipHolidays: VoipHoliday[] = [];
  public voipScheduleRules: VoipScheduleRule[] = [];
  public visibleHolidayModal: boolean = false;
  public holidayDescription: string = '';
  public rangeStartDate: any = null;
  public rangeEndDate: any = null;
  public loading: boolean = false;

  public readonly weekDayOptions = [
    { label: 'Lunes', value: 1 },
    { label: 'Martes', value: 2 },
    { label: 'Miércoles', value: 3 },
    { label: 'Jueves', value: 4 },
    { label: 'Viernes', value: 5 },
    { label: 'Sábado', value: 6 },
    { label: 'Domingo', value: 0 }
  ];

  constructor(
    private voipScheduleRulesService: VoipScheduleRulesService,
    private voipHolidaysService: VoipHolidaysService,
    private messageService: MessageService,
    public loginService: LoginService,
    private topbarService: TopbarService
  ) {
  }

  async ngOnInit() {
    this.topbarService.setTitle('Horario de centralita');
    this.topbarService.addBreadcrumb('Centralita', '/voip/status');
    this.allowWrite = this.loginService.hasPermission('VOIP.SCHEDULE_ACCESS');
    await this.loadConfiguration();
  }

  async loadConfiguration() {
    this.loading = true;

    try {
      const [rulesResponse, holidaysResponse] = await Promise.all([
        firstValueFrom(this.voipScheduleRulesService.all({
          page: { number: 1, size: 1000 },
          sort: ['scheduleType', 'startTime', 'endTime', 'dayOfWeek']
        })),
        firstValueFrom(this.voipHolidaysService.all({
          page: { number: 1, size: 1000 },
          sort: ['date']
        }))
      ]);

      this.voipScheduleRules = rulesResponse.data;
      this.voipHolidays = holidaysResponse.data;

      this.normalRules = this.mapRules(this.voipScheduleRules.filter(rule => rule.scheduleType === 'Normal'));
      this.guardRules = this.mapRules(this.voipScheduleRules.filter(rule => rule.scheduleType === 'Guard'));
      this.holidayEvents = this.voipHolidays.map(holiday => this.mapHolidayToCalendarEvent(holiday));

      if (this.normalRules.length === 0) {
        this.normalRules = [this.createDefaultRule('Normal')];
      }

      if (this.guardRules.length === 0) {
        this.guardRules = [this.createDefaultRule('Guard')];
      }
    } finally {
      this.loading = false;
    }
  }

  async saveNormalRules() {
    await this.saveRulesByType('Normal');
  }

  async saveGuardRules() {
    await this.saveRulesByType('Guard');
  }

  addRule(scheduleType: 'Normal' | 'Guard') {
    if (!this.allowWrite) {
      return;
    }

    const collection = scheduleType === 'Normal' ? this.normalRules : this.guardRules;
    collection.push(this.createDefaultRule(scheduleType));
  }

  removeRule(scheduleType: 'Normal' | 'Guard', index: number) {
    if (!this.allowWrite) {
      return;
    }

    const collection = scheduleType === 'Normal' ? this.normalRules : this.guardRules;
    collection.splice(index, 1);

    if (collection.length === 0) {
      collection.push(this.createDefaultRule(scheduleType));
    }
  }

  async saveRulesByType(scheduleType: 'Normal' | 'Guard') {
    if (!this.allowWrite) {
      return;
    }

    const rules = scheduleType === 'Normal' ? this.normalRules : this.guardRules;
    const invalidRule = rules.find(rule => !rule.weekDays?.length || !rule.startTime || !rule.endTime);

    if (invalidRule) {
      this.messageService.add({ closable: true, severity: 'error', summary: 'Error', detail: 'Revise los días y horas de todas las reglas antes de guardar.' });
      return;
    }

    const payload = this.normalizeRules(rules, scheduleType);

    const currentRules = this.voipScheduleRules.filter(rule => rule.scheduleType === scheduleType);

    await Promise.all(currentRules.map(rule => firstValueFrom(this.voipScheduleRulesService.delete(rule.id))));
    await Promise.all(payload.flatMap(rule =>
      rule.weekDays.map(dayOfWeek => {
        const resource = new VoipScheduleRule();
        resource.scheduleType = rule.scheduleType;
        resource.dayOfWeek = dayOfWeek;
        resource.startTime = rule.startTime;
        resource.endTime = rule.endTime;
        return firstValueFrom(this.voipScheduleRulesService.save(resource));
      })
    ));

    this.messageService.add({ closable: true, severity: 'success', summary: `Horario ${scheduleType === 'Normal' ? 'normal' : 'de guardia'} guardado correctamente.` });
    await this.loadConfiguration();
  }

  daySelected(data: any) {
    if (!this.allowWrite) {
      return;
    }

    this.rangeStartDate = moment(data.date.date).startOf('day');
    this.rangeEndDate = moment(data.date.date).startOf('day');
    this.holidayDescription = '';
    this.visibleHolidayModal = true;
  }

  rangeSelected(data: any) {
    if (!this.allowWrite) {
      return;
    }

    this.rangeStartDate = moment(data.start).startOf('day');
    this.rangeEndDate = moment(data.end).startOf('day');
    this.holidayDescription = '';
    this.visibleHolidayModal = true;
  }

  async saveHolidayRange() {
    if (!this.allowWrite) {
      return;
    }

    const days = this.getRangeDays(this.rangeStartDate, this.rangeEndDate);
    const existingHolidayDates = new Set(this.voipHolidays.map(holiday => moment(holiday.date).format('YYYY-MM-DD')));

    await Promise.all(days
      .filter(day => !existingHolidayDates.has(day.format('YYYY-MM-DD')))
      .map(day => {
        const holiday = new VoipHoliday();
        holiday.date = day.toDate().toISOString();
        holiday.description = this.holidayDescription;
        return firstValueFrom(this.voipHolidaysService.save(holiday));
      }));

    this.closeHolidayModal();
    this.messageService.add({ closable: true, severity: 'success', summary: 'Festivos guardados correctamente.' });
    await this.loadConfiguration();
  }

  async deleteHolidayRange() {
    if (!this.allowWrite) {
      return;
    }

    const days = new Set(this.getRangeDays(this.rangeStartDate, this.rangeEndDate).map(day => day.format('YYYY-MM-DD')));

    await Promise.all(this.voipHolidays
      .filter(holiday => days.has(moment(holiday.date).format('YYYY-MM-DD')))
      .map(holiday => firstValueFrom(this.voipHolidaysService.delete(holiday.id))));

    this.closeHolidayModal();
    this.messageService.add({ closable: true, severity: 'success', summary: 'Festivos eliminados correctamente.' });
    await this.loadConfiguration();
  }

  isSingleSelectedDay(): boolean {
    return !!this.rangeStartDate && !!this.rangeEndDate && moment(this.rangeStartDate).isSame(this.rangeEndDate, 'day');
  }

  closeHolidayModal() {
    this.visibleHolidayModal = false;
    this.holidayDescription = '';
  }

  private mapRules(rules: VoipScheduleRule[]): VoipScheduleRuleRow[] {
    const groupedRules = new Map<string, VoipScheduleRuleRow>();

    rules.forEach(rule => {
      const key = `${rule.scheduleType}|${rule.startTime}|${rule.endTime}`;
      const currentRule = groupedRules.get(key) ?? {
        scheduleType: rule.scheduleType,
        weekDays: [],
        startTime: this.timeStringToDate(rule.startTime),
        endTime: this.timeStringToDate(rule.endTime)
      };

      currentRule.weekDays = [...currentRule.weekDays, rule.dayOfWeek].sort((left, right) => left - right);
      groupedRules.set(key, currentRule);
    });

    return Array.from(groupedRules.values());
  }

  private normalizeRules(rules: VoipScheduleRuleRow[], scheduleType: 'Normal' | 'Guard'): VoipScheduleRuleRow[] {
    return rules.map(rule => ({
      scheduleType,
      weekDays: [...rule.weekDays].sort((left, right) => left - right),
      startTime: this.formatTime(rule.startTime),
      endTime: this.formatTime(rule.endTime)
    }));
  }

  private createDefaultRule(scheduleType: 'Normal' | 'Guard'): VoipScheduleRuleRow {
    const rule = new VoipScheduleRuleRow();
    rule.scheduleType = scheduleType;
    rule.startTime = this.timeStringToDate(scheduleType === 'Normal' ? '09:00:00' : '18:00:00');
    rule.endTime = this.timeStringToDate(scheduleType === 'Normal' ? '18:00:00' : '09:00:00');
    return rule;
  }

  private mapHolidayToCalendarEvent(holiday: VoipHoliday): CalendarEvent {
    const event = new CalendarEvent();
    event.id = holiday.id;
    event.date = holiday.date;
    event.description = holiday.description ?? 'Festivo centralita';
    return event;
  }

  private getRangeDays(startDate: any, endDate: any): moment.Moment[] {
    const start = moment(startDate).startOf('day');
    const end = moment(endDate).startOf('day');
    const from = start.isSameOrBefore(end) ? start : end;
    const to = start.isSameOrBefore(end) ? end : start;
    const days: moment.Moment[] = [];

    for (let current = from.clone(); current.isSameOrBefore(to, 'day'); current = current.clone().add(1, 'day')) {
      days.push(current);
    }

    return days;
  }

  private timeStringToDate(time: any): Date {
    if (time instanceof Date) {
      return time;
    }

    const [hours, minutes, seconds] = `${time ?? '00:00:00'}`.split(':').map(value => Number(value));
    const date = new Date();
    date.setHours(hours || 0, minutes || 0, seconds || 0, 0);
    return date;
  }

  private formatTime(value: any): string {
    return moment(value).format('HH:mm:ss');
  }
}